import React from 'react'
import { Page, Text, View, Document, StyleSheet, Svg, Path, Line, Font } from '@react-pdf/renderer'
import { CORRECTIVE_AND_PREVENTIVE_ACTIONS, CorrectiveAndPreventiveAction, RCA, TRUE_TRANSLATION_ERRORS } from 'api/rca'
import moment from 'moment'
import { Translate } from 'react-admin'
import { ExtendedJobRecord } from 'api/jobs'
import { UserRecord } from 'api/users'
import { parseTimestamp } from 'utils/helpers'
import UserService from 'UserService'

Font.register({
  family: "Inter",
  src: "/fonts/Inter-VariableFont_opsz,wght.ttf"
})

const styles = StyleSheet.create({
  page: {
    padding: 25,
    backgroundColor: '#FFFFFF',
    fontFamily: 'Inter'
  },
  box: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  sectionTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold'
  },
  text: {
    fontSize: 11,
    margin: '10px 0px'
  },
  table: {
    fontSize: 11,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    padding: 0
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "nowrap",
    padding: 0,
    margin: 0
  },
  cell: {
    flexGrow: 1,
    flexBasis: 100
  },
  staticCellPaddingAndMargin: {
    padding: '0px 10px',
    margin: 0
  },
  inputCellPaddingAndMargin: {
    padding: '1px 3px',
    margin: 0
  },
  checkboxCellCenter: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around"
  },
  capaCell: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexBasis: 100,
    margin: 'auto'
  },
  otherCell: {
    display: "flex",
    flexDirection: "column"
  },
  textCenter: {
    textAlign: "center"
  },
  textRight: {
    textAlign: "right"
  },
  firstRowBorders: {
    borderColor: "#000000",
    borderStyle: "solid",
    borderWidth: 1
  },
  rowBorders: {
    borderColor: "#000000",
    borderStyle: "solid",
    borderWidth: 1,
    borderTop: 0
  },
  cellRightBorder: {
    borderColor: "#000000",
    borderStyle: "solid",
    borderRight: 1
  },
  rowMargin: {
    marginBottom: 8
  },
  firstColumnWidth: {
    width: '15%'
  },
  secondColumnWidth: {
    width: '45%'
  },
  thirdColumnWidth: {
    width: '20%'
  },
  fourthColumnWidth: {
    width: '20%'
  }
})

export const RCARaportPDF = (rcaRecord: RCA, job: ExtendedJobRecord, requester: UserRecord, translate: Translate) => {
  const { zendeskTicketNumber, requestDate, implementationDate, issue, justification, exfluencyContactPerson,
    capaOtherContent, comments, trueTranslationErrors, correctiveAndPreventiveActions, rcaResponsibleUsersByStageIds } = rcaRecord
  const { name, externalProjectId, sourceLang, targetLang, stages } = job
  const { timezone } = UserService.getTokenUserInfo()
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.box}>
          <Svg width="28" height="28" viewBox="0 0 28 28" style={{ margin: 5 }}>
            <Path d="M24.9785 18.6061L25.1946 18.6149L25.2734 18.4136C25.8082 17.0481 26.0927 15.5412 26.0927 14.0001C26.0927 9.73187 23.9216 5.96671 20.6492 3.74401L20.186 3.42943L20.1753 3.9892C20.1408 5.80061 20.0025 8.72443 19.554 11.7149C19.2781 13.453 18.9341 14.9758 18.5238 16.251L18.4553 16.4639L18.6384 16.5923C20.2855 17.7471 22.3556 18.4989 24.9785 18.6061ZM18.8383 2.94551V2.7416L18.6495 2.66458C17.16 2.05691 15.5592 1.69757 13.8839 1.69757C7.15393 1.69757 1.67504 7.223 1.67504 14.0001C1.67504 16.8685 2.63637 19.4916 4.38386 21.5763L4.53308 21.7543L4.74387 21.6567C6.06035 21.0471 7.02739 20.3267 7.74737 19.6011L7.96396 19.3828L7.74277 19.1692C6.10297 17.5853 4.93177 15.7129 4.60116 14.1138C4.17848 12.0485 5.11335 10.4138 6.80823 9.73536C7.63069 9.42995 8.72939 9.64235 9.48745 10.2782C10.806 11.4137 11.4898 13.706 11.0858 16.0474C10.9523 16.8212 10.6513 17.8333 10.0148 18.8799L9.86755 19.1221L10.0991 19.2855C11.1709 20.0416 12.3466 20.4986 13.3745 20.4602L13.3785 20.46C14.9622 20.3802 16.0693 18.9532 16.837 17.0891L16.9233 16.8796L16.7469 16.7374C16.5032 16.5409 16.3007 16.3309 16.079 16.1011C16.0018 16.0211 15.9223 15.9387 15.8379 15.8536C13.7301 13.5969 12.5749 10.7702 11.8421 8.6975L13.1397 8.26159C13.9692 10.6419 15.1165 13.1903 16.9968 15.1544L17.3861 15.561L17.5117 15.0123L17.6679 14.3301L17.8241 13.648L17.8241 13.648L17.825 13.6441C18.6297 9.90611 18.8383 5.50922 18.8383 2.94551ZM9.63518 15.8397L9.6353 15.839C9.81691 14.8506 9.7631 13.8261 9.54364 12.9875C9.43383 12.5678 9.28026 12.1859 9.08683 11.8752C8.89627 11.5691 8.65482 11.3129 8.3599 11.1739C7.9187 10.9479 7.49163 10.8874 7.10282 10.9768C6.71447 11.0661 6.39567 11.2976 6.15722 11.5993C5.68648 12.1948 5.50105 13.0939 5.67299 13.9218L5.67306 13.9222C5.97058 15.3465 7.14019 16.8349 8.3988 18.0329L8.68606 18.3064L8.87482 17.9576C9.30813 17.1569 9.52597 16.4267 9.63518 15.8397ZM5.79589 22.6512L5.41177 22.85L5.72946 23.1435C7.86749 25.1188 10.7523 26.3025 13.8839 26.3025C18.2991 26.3025 22.1773 23.9339 24.3466 20.4214L24.6037 20.0052L24.1166 19.9599C21.9048 19.7541 19.9739 19.1038 18.2829 18.013L17.991 17.8247L17.8437 18.1392C16.6895 20.6027 15.2055 21.7863 13.3818 21.883C12.1177 21.9482 10.6211 21.4237 9.16035 20.4308L8.94717 20.2859L8.76972 20.4729C8.03134 21.2509 7.05267 22.0007 5.79589 22.6512ZM0.303404 14.0001C0.303404 6.43419 6.38283 0.310362 13.8839 0.310362C21.3507 0.310362 27.4643 6.43468 27.4643 14.0001C27.4643 21.5309 21.3502 27.6897 13.8839 27.6897C6.38332 27.6897 0.303404 21.5314 0.303404 14.0001Z" fill="#3A65F6" stroke="#3A65F6" stroke-width="0.606809"></Path>
          </Svg>
          <Svg width="94" height="20" viewBox="0 0 94 20" style={{ margin: 5 }}>
            <Path d="M6.26945 15.1576C5.15874 15.1576 4.15216 14.9127 3.31913 14.4579C2.48609 14.0031 1.82661 13.3385 1.34068 12.5339C0.854742 11.7292 0.646484 10.7847 0.646484 9.73523C0.646484 8.65076 0.889452 7.67125 1.34068 6.86665C1.7919 6.02706 2.41667 5.36239 3.215 4.90761C4.01332 4.41785 4.95048 4.17297 5.99177 4.17297C6.92893 4.17297 7.76196 4.41785 8.52557 4.87263C9.25448 5.3274 9.84454 5.95709 10.2611 6.72671C10.6776 7.49633 10.8858 8.40589 10.8511 9.42039L10.8164 10.295H3.28442C3.42326 11.0646 3.80506 11.6593 4.36042 12.0791C4.95048 12.5339 5.67938 12.7437 6.54712 12.7437C7.03306 12.7437 7.44957 12.6738 7.83138 12.5339C8.21319 12.3939 8.6297 12.1141 9.08093 11.7642L10.3999 13.6183C9.84454 14.1081 9.18506 14.5279 8.45615 14.7727C7.72725 15.0176 6.99835 15.1576 6.26945 15.1576ZM5.99177 6.51682C4.49925 6.51682 3.5968 7.18149 3.28442 8.54582H8.24789V8.47585C8.21318 7.91613 7.97022 7.42637 7.5537 7.07654C7.13719 6.72671 6.61654 6.51682 5.99177 6.51682Z" fill="black"></Path>
            <Path d="M11.5781 14.9475L15.5003 9.59517L11.6475 4.41772H14.945L17.2358 7.63614L19.1101 4.41772H22.2687L18.6589 9.59517L22.5811 14.9475H19.3531L16.9581 11.5892L14.8061 14.9475H11.5781Z" fill="black"></Path>
            <Path d="M24.9775 14.9476V7.00652H23.3809V4.66268H24.9775V3.61319C24.9775 2.94852 25.1163 2.3888 25.394 1.82907C25.6717 1.30433 26.0535 0.88454 26.5741 0.604678C27.0948 0.289833 27.6849 0.149902 28.3443 0.149902C28.7956 0.149902 29.2468 0.219868 29.6286 0.394781C30.0104 0.534712 30.3575 0.744609 30.6699 1.02447L29.8716 3.05347C29.698 2.94852 29.5245 2.84357 29.3509 2.77361C29.1774 2.70364 29.0038 2.66866 28.8303 2.66866C28.0667 2.66866 27.6848 3.05347 27.6848 3.82309V4.69766H29.9757V7.0415H27.6848V14.9476H24.9775Z" fill="black"></Path>
            <Path d="M31.7129 14.9478V0.185059H34.4203V14.9478H31.7129Z" fill="black"></Path>
            <Path d="M40.563 15.1574C39.4176 15.1574 38.5151 14.7726 37.8557 14.038C37.1962 13.3033 36.8838 12.2539 36.8838 10.9245V4.41772H39.5911V10.4348C39.5911 11.2044 39.7647 11.7991 40.1465 12.2189C40.4936 12.6387 41.0142 12.8835 41.639 12.8835C42.2638 12.8835 42.7844 12.6737 43.2009 12.2888C43.6174 11.904 43.8257 11.3793 43.8257 10.7496V4.45271H46.5331V14.9475H43.9645L43.8951 13.4783C43.548 13.968 43.0621 14.3878 42.472 14.7027C41.882 15.0175 41.2225 15.1574 40.563 15.1574Z" fill="black"></Path>
            <Path d="M54.0634 15.1576C52.9527 15.1576 51.9461 14.9127 51.1131 14.4579C50.28 14.0031 49.6206 13.3385 49.1346 12.5339C48.6487 11.7292 48.4404 10.7847 48.4404 9.73523C48.4404 8.65076 48.6834 7.67125 49.1346 6.86665C49.5858 6.02706 50.2106 5.36239 51.0089 4.90761C51.8073 4.41785 52.7444 4.17297 53.7857 4.17297C54.7229 4.17297 55.5559 4.41785 56.3195 4.87263C57.0484 5.3274 57.6385 5.95709 58.055 6.72671C58.4715 7.49633 58.6798 8.40589 58.6451 9.42039L58.6103 10.295H51.0784C51.2172 11.0646 51.599 11.6593 52.1544 12.0791C52.7444 12.5339 53.4733 12.7437 54.3411 12.7437C54.827 12.7437 55.2435 12.6738 55.6253 12.5339C56.0071 12.3939 56.4236 12.1141 56.8749 11.7642L58.1938 13.6183C57.6385 14.1081 56.979 14.5279 56.2501 14.7727C55.5212 15.0176 54.7923 15.1576 54.0634 15.1576ZM53.7857 6.51682C52.2932 6.51682 51.3907 7.18149 51.0784 8.54582H56.0418V8.47585C56.0071 7.91613 55.7642 7.42637 55.3476 7.07654C54.9311 6.72671 54.4105 6.51682 53.7857 6.51682Z" fill="black"></Path>
            <Path d="M60.5205 14.9478V4.41803H63.1237L63.1584 5.85232C63.5055 5.36256 63.9915 4.97775 64.5815 4.66291C65.1716 4.34806 65.7964 4.20813 66.4559 4.20813C68.8508 4.20813 70.0657 5.64242 70.0657 8.51101V14.9478H67.3583V8.7209C67.3583 7.21665 66.7335 6.48201 65.484 6.55197C64.8245 6.55197 64.3039 6.76187 63.8526 7.14668C63.4361 7.53149 63.1932 8.05623 63.1932 8.61596V14.9478H60.5205Z" fill="black"></Path>
            <Path d="M77.0761 15.1576C76.0695 15.1576 75.2018 14.9127 74.4035 14.4229C73.6052 13.9332 72.9804 13.3035 72.5292 12.4639C72.0779 11.6243 71.835 10.7147 71.835 9.66527C71.835 8.61578 72.0779 7.70623 72.5292 6.86665C72.9804 6.02706 73.6052 5.39737 74.4035 4.90761C75.2018 4.41785 76.0695 4.17297 77.0761 4.17297C78.0133 4.17297 78.881 4.34789 79.6446 4.69771C80.4082 5.04754 81.033 5.5373 81.4495 6.16699L79.957 7.95111C79.6793 7.5663 79.2975 7.25146 78.8116 6.97159C78.3257 6.69173 77.8397 6.5518 77.3191 6.5518C76.7637 6.5518 76.2778 6.69173 75.8266 6.97159C75.4101 7.25146 75.063 7.60128 74.82 8.09104C74.577 8.54582 74.4382 9.07056 74.4382 9.66527C74.4382 10.26 74.577 10.7847 74.82 11.2395C75.063 11.6943 75.4101 12.0791 75.8613 12.3589C76.3125 12.6388 76.7984 12.7787 77.3191 12.7787C77.8397 12.7787 78.3257 12.6738 78.7422 12.4289C79.1934 12.184 79.5752 11.8692 79.9223 11.4494L81.4148 13.2335C80.9636 13.8282 80.3388 14.283 79.5405 14.6328C78.8116 14.9826 77.9786 15.1576 77.0761 15.1576Z" fill="black"></Path>
            <Path d="M84.2607 19.5653L86.2044 15.0175L81.4492 4.41772H84.5037L87.1763 10.7146C87.2804 10.9945 87.4193 11.3093 87.5234 11.6941C87.5928 11.5542 87.6275 11.4143 87.6622 11.2743C87.6969 11.1344 87.7664 10.9945 87.8011 10.8196L90.0919 4.41772H93.2158L88.8077 14.9475L87.0028 19.5303H84.2607V19.5653Z" fill="black"></Path>
          </Svg>
        </View>
        <ShowLine />
        <View>
          <Text style={[styles.sectionTitle, styles.textCenter]}>EXFLUENCY ROOT CAUSE ANALYSIS</Text>
        </View>

        <View style={[styles.table, { margin: '15px 0px' }]}>
          <View style={[styles.row, styles.firstRowBorders]}>
            <Text style={[styles.staticCellPaddingAndMargin, styles.firstColumnWidth, styles.cellRightBorder]}>Project name:</Text>
            <Text style={[styles.inputCellPaddingAndMargin, styles.secondColumnWidth, styles.cellRightBorder]}>{name}</Text>
            <Text style={[styles.staticCellPaddingAndMargin, styles.thirdColumnWidth, styles.cellRightBorder]}>Date:</Text>
            <Text style={[styles.inputCellPaddingAndMargin, styles.fourthColumnWidth]}>{moment(requestDate).format('DD.MM.YYYY')}</Text>
          </View>
          <View style={[styles.row, styles.rowBorders]}>
            <Text style={[styles.staticCellPaddingAndMargin, styles.firstColumnWidth, styles.cellRightBorder]}>External ID:</Text>
            <Text style={[styles.inputCellPaddingAndMargin, styles.secondColumnWidth, styles.cellRightBorder]}>{externalProjectId}</Text>
            <Text style={[styles.staticCellPaddingAndMargin, styles.thirdColumnWidth, styles.cellRightBorder]}>Ticket #:</Text>
            <Text style={[styles.inputCellPaddingAndMargin, styles.fourthColumnWidth]}>{zendeskTicketNumber}</Text>
          </View>
          <View style={[styles.row, styles.rowBorders]}>
            <Text style={[styles.staticCellPaddingAndMargin, styles.firstColumnWidth, styles.cellRightBorder]}>Requester:</Text>
            <Text style={[styles.inputCellPaddingAndMargin, styles.secondColumnWidth, styles.cellRightBorder]}>{requester?.firstName} {requester?.lastName}</Text>
            <Text style={[styles.staticCellPaddingAndMargin, styles.thirdColumnWidth, styles.cellRightBorder]}>Language{'\n'}combination:</Text>
            <Text style={[styles.inputCellPaddingAndMargin, styles.fourthColumnWidth]}>{sourceLang} - {targetLang}</Text>
          </View>
          {stages.filter(stage => ['REDACTION', 'ENHANCEMENT', 'TRUST_MINING', 'FINAL_REDACTION'].includes(stage.type)).map(
            ({ type, id: stageId, assignments }) => {
              const assignment = assignments.find(({ status }) => ['ACCEPTED', 'DELIVERED', 'CANCELLED', 'FINISHED', 'INTERRUPTED'].includes(status))
              return assignment?.executorId && <View key={stageId} style={[styles.row, styles.rowBorders]}>
                <Text style={[styles.staticCellPaddingAndMargin, styles.firstColumnWidth, styles.cellRightBorder]}>User ID</Text>
                <Text style={[styles.inputCellPaddingAndMargin, styles.secondColumnWidth, styles.cellRightBorder]}>
                  {rcaResponsibleUsersByStageIds.find(it => it.stageId == stageId) ? assignment.executorId : ''}
                </Text>
                <Text style={[styles.staticCellPaddingAndMargin, styles.thirdColumnWidth, styles.cellRightBorder]}>User&apos;s role</Text>
                <Text style={[styles.inputCellPaddingAndMargin, styles.fourthColumnWidth]}>{translate(`rca.rcaResposibleUserByStageType.${type}`)}</Text>
              </View>
            }
          )}
          <View style={[styles.row, styles.rowBorders]}>
            <Text style={[styles.cell, styles.sectionTitle, styles.textCenter]}>True Translation Errors (TTE)</Text>
          </View>
          <View style={[styles.row, styles.rowBorders]}>
            {TRUE_TRANSLATION_ERRORS.map((tte, idx) => (
              <View key={idx+tte} style={[styles.cell, idx < 4 ? styles.cellRightBorder : {}]}>
                <Text style={[ { width: '80px', margin: 'auto' }, styles.textCenter]}>
                  {translate(`rca.trueTranslationError.${tte}`)}
                </Text>
              </View>
            ))}
          </View>
          <View style={[styles.row, styles.rowBorders]}>
            {TRUE_TRANSLATION_ERRORS.map((tte, idx) => (
              <View key={tte} style={[styles.cell, styles.checkboxCellCenter, idx < 4 ? styles.cellRightBorder : {}]}>
                {showCheckboxCrossedOrNot(trueTranslationErrors.includes(tte))}
              </View>
            ))}
          </View>
        </View>

        <View wrap={false}>
          <Text style={[styles.sectionTitle, styles.textCenter]}>Issue</Text>
          <Text style={styles.text}>{issue}</Text>
        </View>
        
        <View wrap={false}>
          <Text style={[styles.sectionTitle, styles.textCenter]}>Root Cause</Text>
          <Text style={styles.text}>{justification}</Text>
        </View>

        <View wrap={false}>
          <Text style={[styles.sectionTitle, styles.textCenter]}>CAPA</Text>
          <View style={[styles.table, { margin: '15px 0px' }]}>
            {showCapaCellsInRow(0, 4, correctiveAndPreventiveActions, translate)}
            {showCapaCellsInRow(4, 8, correctiveAndPreventiveActions, translate)}
            {showCapaCellsInRow(8, 12, correctiveAndPreventiveActions, translate)}
            <View style={[styles.row, { borderColor: "#000000", borderStyle: "solid", borderTop: 1 }]}>
              <View style={[styles.otherCell, { width: '25%', margin: '5px 0px' }]}>
                <Text style={[styles.textCenter]}>{translate(`rca.correctiveAndPreventiveAction.${'OTHER'}`)}</Text>
                <View style={[styles.checkboxCellCenter]}>
                  {showCheckboxCrossedOrNot(correctiveAndPreventiveActions.includes('OTHER'))}
                </View>
              </View>
              <Text style={[{ width: '75%', fontSize: 11, margin: '5px 0px 0px 0px' }]}>{capaOtherContent}</Text>
            </View>
          </View>
        </View>

        <View wrap={false}>
          <Text style={[styles.sectionTitle, styles.textCenter]}>Comments</Text>
          <Text style={styles.text}>{comments}</Text>
        </View>

        <View wrap={false} style={[styles.table, { margin: '10px 0px 5px 0px' }]}>
          <View style={[styles.row]}>
            <Text style={[styles.cell, styles.staticCellPaddingAndMargin, styles.sectionTitle, styles.textRight]}>Resolution / Implementation date:</Text>
            <Text style={[styles.cell, styles.staticCellPaddingAndMargin]}>{parseTimestamp(implementationDate, timezone)}</Text>
          </View>
          <View style={[styles.row]}>
            <Text style={[styles.cell, styles.staticCellPaddingAndMargin, styles.sectionTitle, styles.textRight]}>Exfluency contact person:</Text>
            <Text style={[styles.cell, styles.staticCellPaddingAndMargin]}>{exfluencyContactPerson}</Text>
          </View>
        </View>
        <ShowLine />
      </Page>
    </Document>
  )
}

const ShowLine = () => <Svg viewBox="0 0 100 1"><Line x1="0" y1="0" x2="100" y2="0" stroke="black" /></Svg>

const showCheckboxCrossedOrNot = (isChecked: boolean) => 
    isChecked ? <Svg width="14" height="14" viewBox="0 0 24 24"><Path d="M18.25,3 C19.7687831,3 21,4.23121694 21,5.75 L21,18.25 C21,19.7687831 19.7687831,21 18.25,21 L5.75,21 C4.23121694,21 3,19.7687831 3,18.25 L3,5.75 C3,4.23121694 4.23121694,3 5.75,3 L18.25,3 Z M18.25,4.5 L5.75,4.5 C5.05964406,4.5 4.5,5.05964406 4.5,5.75 L4.5,18.25 C4.5,18.9403559 5.05964406,19.5 5.75,19.5 L18.25,19.5 C18.9403559,19.5 19.5,18.9403559 19.5,18.25 L19.5,5.75 C19.5,5.05964406 18.9403559,4.5 18.25,4.5 Z M10,14.4393398 L16.4696699,7.96966991 C16.7625631,7.6767767 17.2374369,7.6767767 17.5303301,7.96966991 C17.7965966,8.23593648 17.8208027,8.65260016 17.6029482,8.94621165 L17.5303301,9.03033009 L10.5303301,16.0303301 C10.2640635,16.2965966 9.84739984,16.3208027 9.55378835,16.1029482 L9.46966991,16.0303301 L6.46966991,13.0303301 C6.1767767,12.7374369 6.1767767,12.2625631 6.46966991,11.9696699 C6.73593648,11.7034034 7.15260016,11.6791973 7.44621165,11.8970518 L7.53033009,11.9696699 L10,14.4393398 L16.4696699,7.96966991 L10,14.4393398 Z" fill="black"></Path></Svg> :
    <Svg width="14" height="14" viewBox="0 0 24 24"><Path d="M5.75,3 L18.25,3 C19.7687831,3 21,4.23121694 21,5.75 L21,18.25 C21,19.7687831 19.7687831,21 18.25,21 L5.75,21 C4.23121694,21 3,19.7687831 3,18.25 L3,5.75 C3,4.23121694 4.23121694,3 5.75,3 Z M5.75,4.5 C5.05964406,4.5 4.5,5.05964406 4.5,5.75 L4.5,18.25 C4.5,18.9403559 5.05964406,19.5 5.75,19.5 L18.25,19.5 C18.9403559,19.5 19.5,18.9403559 19.5,18.25 L19.5,5.75 C19.5,5.05964406 18.9403559,4.5 18.25,4.5 L5.75,4.5 Z" fill="black"></Path></Svg>

const showCapaCellsInRow = (capaStartIdx: number, capaEndIdx: number, correctiveAndPreventiveActions: CorrectiveAndPreventiveAction[], translate: any) => (
  <View style={[styles.row, styles.rowMargin]}>
    {CORRECTIVE_AND_PREVENTIVE_ACTIONS.slice(capaStartIdx, capaEndIdx).map((capa, idx) => (
      <View key={idx+capa} style={[styles.capaCell]}>
        {capa != 'OTHER' && <>
          <Text style={[styles.textCenter]}>{translate(`rca.correctiveAndPreventiveAction.${capa}`)}</Text>
          <View style={[styles.checkboxCellCenter]}>
            {showCheckboxCrossedOrNot(correctiveAndPreventiveActions.includes(capa))}
          </View>
        </>}
      </View>
    ))}
  </View>
)
